import React from "react";
import { observer } from "mobx-react";
import {
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  useTheme,
} from "@mui/material";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import {
  PodRevenueSortBy,
  PodRevenueSortType,
} from "../../../../../../models/Dashboard/PodRevenue/PodRevenueFilters";
import { PodRevenueLineRow } from "../../../../../../models/Dashboard/PodRevenue/PodRevenueSummary";
import {
  formatPodCount,
  formatPodDateTime,
  formatPodMoney,
} from "./podRevenueFormat";
import { PodRevenueEmptyState, podHeaderCellSx } from "./PodRevenueSection";
import PodRevenuePager, { PodRevenueTableProgress } from "./PodRevenuePager";

interface Props {
  rows: PodRevenueLineRow[];
  page: number;
  pages: number;
  total?: number;
  loading: boolean;
  error: string | null;
  sortBy: PodRevenueSortBy;
  sortType: PodRevenueSortType;
  onSort: (sort: PodRevenueSortBy) => void;
  onPage: (page: number) => void;
  onRetry?: () => void;
  hideProduct?: boolean;
  /** Hide Order/Customer columns (order drawer: already in the header). */
  hideOrder?: boolean;
  /** Hide handling/shipping (order services; shown as order totals instead). */
  hideOrderCharges?: boolean;
  maxHeight?: number;
}

const COLS: Array<{
  id: string;
  label: string;
  sort?: PodRevenueSortBy;
  product?: boolean;
  order?: boolean;
  charge?: boolean;
  align?: "left" | "center" | "right";
}> = [
  { id: "order", label: "Order #", order: true, align: "center" },
  { id: "cart", label: "Cart", order: true, align: "center" },
  { id: "customer", label: "Customer", order: true, align: "center" },
  { id: "store", label: "Store", order: true, align: "center" },
  { id: "product", label: "Product", product: true, align: "center" },
  { id: "sku", label: "SKU", product: true, align: "center" },
  { id: "qty", label: "Qty", sort: "units", align: "center" },
  { id: "unit_price", label: "Unit price", align: "center" },
  { id: "gst_pct", label: "GST %", align: "center" },
  {
    id: "selling_exclusive",
    label: "Selling excl.",
    sort: "selling_exclusive",
    align: "center",
  },
  { id: "gst", label: "GST", sort: "gst", align: "center" },
  {
    id: "handling",
    label: "Handling",
    sort: "handling",
    align: "center",
    charge: true,
  },
  {
    id: "shipping",
    label: "Shipping",
    sort: "shipping",
    align: "center",
    charge: true,
  },
  {
    id: "gross_revenue",
    label: "Gross",
    sort: "gross_revenue",
    align: "center",
  },
  { id: "date", label: "Date", sort: "order_date", order: true, align: "center" },
];

const num = { fontVariantNumeric: "tabular-nums" as const };

/** Line-level listing (order × product). Used on the page and inside the product drawer. */
const PodRevenueLinesTable: React.FC<Props> = observer((props) => {
  const {
    rows,
    page,
    pages,
    total,
    loading,
    error,
    sortBy,
    sortType,
    onSort,
    onPage,
    onRetry,
    hideProduct,
    hideOrder,
    hideOrderCharges,
    maxHeight = 480,
  } = props;
  const colors = useTheme().palette.customColors;
  const cols = COLS.filter(
    (c) =>
      !(hideProduct && c.product) &&
      !(hideOrder && c.order) &&
      !(hideOrderCharges && c.charge),
  ).map((c) =>
    hideOrderCharges && c.id === "gross_revenue"
      ? { ...c, label: "Line total" }
      : c,
  );
  const cellSx = { borderColor: colors.lightGray, whiteSpace: "nowrap" as const };

  if (error)
    return <PodRevenueEmptyState error message={error} onRetry={onRetry} />;
  if (rows.length === 0 && !loading)
    return (
      <PodRevenueEmptyState message="No POD order lines for these filters." />
    );

  const showSkeleton = loading && rows.length === 0;

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        borderColor: colors.lightGray,
        overflow: "hidden",
      }}
    >
      <PodRevenueTableProgress loading={loading && !showSkeleton} />
      <TableContainer
        sx={{
          maxHeight,
          overflowX: "auto",
          opacity: loading && !showSkeleton ? 0.6 : 1,
          transition: "opacity .15s ease",
        }}
      >
        <Table stickyHeader size="small" sx={{ minWidth: "max-content" }}>
          <TableHead>
            <TableRow>
              {cols.map((col) => (
                <TableCell
                  key={col.id}
                  align={col.align ?? "left"}
                  sortDirection={
                    col.sort && sortBy === col.sort
                      ? sortType === "DESC"
                        ? "desc"
                        : "asc"
                      : false
                  }
                  sx={{
                    ...podHeaderCellSx,
                    whiteSpace: "nowrap",
                    color: colors.greyTextColor,
                    bgcolor: colors.white,
                    borderColor: colors.lightGray,
                  }}
                >
                  {col.sort ? (
                    <TableSortLabel
                      active={sortBy === col.sort}
                      direction={
                        sortBy === col.sort && sortType === "ASC"
                          ? "asc"
                          : "desc"
                      }
                      onClick={() => onSort(col.sort as PodRevenueSortBy)}
                      sx={
                        col.align === "center"
                          ? {
                              justifyContent: "center",
                              width: "100%",
                              position: "relative",
                              "& .MuiTableSortLabel-icon": {
                                position: "absolute",
                                right: 0,
                              },
                            }
                          : undefined
                      }
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : (
                    col.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {showSkeleton
              ? Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={`sk-${i}`}>
                    {cols.map((c) => (
                      <TableCell key={c.id} align={c.align ?? "left"} sx={cellSx}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : rows.map((row, idx) => (
                  <TableRow
                    key={`${row.order_id}-${row.product_id}-${row.sku ?? ""}-${idx}`}
                    hover
                    sx={{
                      "&:nth-of-type(even)": {
                        bgcolor: colors.softCardBackground,
                      },
                      transition: "background-color .12s ease",
                    }}
                  >
                    {!hideOrder ? (
                      <>
                        <TableCell align="center" sx={cellSx}>
                          <CustomTypography variant="heading7" fontWeight={600} color={colors.navyText} sx={num}>
                            #{row.order_id}
                          </CustomTypography>
                        </TableCell>
                        <TableCell align="center" sx={{ ...cellSx, ...num }}>
                          {row.cart_id || "—"}
                        </TableCell>
                        <TableCell align="center" sx={cellSx}>
                          {row.customer_name || "—"}
                        </TableCell>
                        <TableCell align="center" sx={cellSx}>{row.store_name || "—"}</TableCell>
                      </>
                    ) : null}
                    {!hideProduct ? (
                      <>
                        <TableCell align="center" sx={cellSx}>
                          <CustomTypography variant="heading7" fontWeight={500}>
                            {row.product_name ||
                              (row.product_id ? `Product #${row.product_id}` : "—")}
                          </CustomTypography>
                        </TableCell>
                        <TableCell align="center" sx={cellSx}>{row.sku || "—"}</TableCell>
                      </>
                    ) : null}
                    <TableCell align="center" sx={{ ...cellSx, ...num }}>
                      {formatPodCount(row.quantity)}
                    </TableCell>
                    <TableCell align="center" sx={{ ...cellSx, ...num }}>
                      {formatPodMoney(row.selling_price)}
                    </TableCell>
                    <TableCell align="center" sx={{ ...cellSx, ...num }}>
                      {row.gst_percentage != null ? `${row.gst_percentage}%` : "—"}
                    </TableCell>
                    <TableCell align="center" sx={{ ...cellSx, ...num }}>
                      {formatPodMoney(row.selling_exclusive)}
                    </TableCell>
                    <TableCell align="center" sx={{ ...cellSx, ...num }}>
                      {formatPodMoney(row.gst_amount)}
                    </TableCell>
                    {!hideOrderCharges ? (
                      <>
                        <TableCell align="center" sx={{ ...cellSx, ...num }}>
                          {formatPodMoney(row.handling_amount)}
                        </TableCell>
                        <TableCell align="center" sx={{ ...cellSx, ...num }}>
                          {formatPodMoney(row.shipping_amount)}
                        </TableCell>
                      </>
                    ) : null}
                    <TableCell align="center" sx={cellSx}>
                      <CustomTypography
                        variant="heading7"
                        fontWeight={700}
                        color={colors.navyText}
                        sx={num}
                      >
                        {formatPodMoney(
                          hideOrderCharges
                            ? Number(row.selling_exclusive || 0) +
                                Number(row.gst_amount || 0)
                            : row.gross_revenue,
                        )}
                      </CustomTypography>
                    </TableCell>
                    {!hideOrder ? (
                      <TableCell align="center" sx={{ ...cellSx, ...num }}>
                        {formatPodDateTime(row.order_date)}
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>
      <PodRevenuePager
        page={page}
        pages={pages}
        loading={loading}
        onPage={onPage}
        summary={total != null ? `${formatPodCount(total)} lines` : undefined}
      />
    </Paper>
  );
});

export default PodRevenueLinesTable;
